/* Harper & Stone — the appointment request.
 *
 * Five steps in a native <dialog>. The panels sit side by side on one track;
 * on a wide screen two are visible — the step just finished on the left,
 * inert and dimmed, and the current one on the right — and moving on slides
 * the track one panel to the left, which is the reference's "right becomes
 * left". On a phone one panel fills the screen and the same track slides a
 * full width at a time.
 *
 * Opening is delegated: any link whose href ends in #appointment, or any
 * [data-appointment-open]. Without this script those links go to the contact
 * page, so nothing on the site depends on it.
 */

const WIDE = matchMedia('(min-width: 64rem)');
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const LAST = 5;
const FLAG = 'hs:appointment-sent';
/* Month and weekday names follow the store's language, not a hardcoded list. */
const LANG = document.documentElement.lang || 'en-US';

/* Every word the script writes comes from the section's settings, through
 * the JSON block it renders. These are only the fallbacks for a setting
 * the merchant has emptied. */
const DEFAULTS = {
  step_count: 'Step [step] of [total]',
  continue: 'Continue',
  send: 'Send request',
  sending: 'Sending',
  change: 'Change',
  phone_way: 'Phone call',
  error_service: 'Please choose a service.',
  error_day: 'Please choose a day.',
  error_time: 'Please choose a part of the day.',
  error_how: 'Please tell us how you would like to meet.',
  error_first: 'Please enter your first name.',
  error_email: 'Please enter a valid email address.',
  error_phone: 'You chose a phone call, so we need a number to reach you.',
  error_consent: 'Please tick the box so we can use your details to arrange this.',
  review_service: 'Service',
  review_interests: 'Interested in',
  review_budget: 'Budget',
  review_occasion: 'Occasion',
  review_when: 'When',
  review_how: 'How',
  review_name: 'Name',
  review_email: 'Email',
  review_phone: 'Phone',
  review_about: 'About',
};

class HsAppointment extends HTMLElement {
  step = 1;

  connectedCallback() {
    this.dialog = this.querySelector('dialog.appt');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    let strings = {};
    try {
      strings = JSON.parse(this.querySelector('[data-appt-strings]')?.textContent || '{}');
    } catch {
      /* A broken block falls back to the defaults rather than a dead dialog. */
    }
    this.text = (key) => strings[key] || DEFAULTS[key];

    this.form = this.querySelector('.appt__form');
    this.track = this.querySelector('[data-appt-track]');
    this.panels = [...this.querySelectorAll('[data-appt-panel]')];
    this.nextButton = this.querySelector('[data-appt-next]');
    this.backButton = this.querySelector('[data-appt-back]');
    this.count = this.querySelector('[data-appt-count]');
    this.error = this.querySelector('[data-appt-error]');
    this.quit = this.querySelector('[data-appt-quit]');

    this.onDocClick = (e) => {
      const opener = e.target.closest?.('a[href$="#appointment"], [data-appointment-open]');
      if (!opener) return;
      e.preventDefault();
      this.opener = opener;
      this.open();
    };
    document.addEventListener('click', this.onDocClick);

    /* In the theme editor the dialog is otherwise invisible: open it while
     * its section is selected, so the merchant sees the words they edit. */
    this.editor = new AbortController();
    const mine = (e) => e.target instanceof Element && e.target.contains(this);
    document.addEventListener('shopify:section:select', (e) => mine(e) && this.open(), { signal: this.editor.signal });
    document.addEventListener('shopify:section:deselect', (e) => mine(e) && this.dialog.open && this.#close(), { signal: this.editor.signal });

    this.nextButton.addEventListener('click', () => this.#next());
    this.backButton.addEventListener('click', () => this.#go(this.step - 1));
    this.querySelector('[data-appt-close]').addEventListener('click', () => this.#requestClose());
    this.querySelector('[data-appt-finish]').addEventListener('click', () => this.#close());
    this.querySelector('[data-appt-stay]').addEventListener('click', () => this.quit.close());
    this.querySelector('[data-appt-leave]').addEventListener('click', () => {
      this.quit.close();
      this.#close(true);
    });

    /* Escape asks first, like the close button. `cancel` fires before the
     * dialog closes and can be prevented. */
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#requestClose();
    });

    /* Enter in a text field would submit the hidden form mid-flow. It moves
     * on instead, which is what someone pressing Enter means. */
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#next();
    });

    /* Picking a service is the whole of step one, so it moves on by itself. */
    for (const radio of this.querySelectorAll('[name="appt_service"]')) {
      radio.addEventListener('change', () => setTimeout(() => this.step === 1 && this.#next(), 260));
    }

    this.form.addEventListener('input', () => this.#clearError());
    this.form.addEventListener('change', () => this.#clearError());

    WIDE.addEventListener('change', () => this.#place(false));
    this.#calendar();
    this.#place(false);

    /* Came back from Shopify's own challenge page after a native submit. */
    if (window.HS?.appointmentPosted && sessionStorage.getItem(FLAG)) {
      sessionStorage.removeItem(FLAG);
      this.dialog.showModal();
      this.#done();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    this.editor?.abort();
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    this.#place(false);
  }

  get #dirty() {
    if (this.finished) return false;
    return this.step > 1 || !!this.form.querySelector('[name="appt_service"]:checked');
  }

  #requestClose() {
    if (this.#dirty) this.quit.showModal();
    else this.#close();
  }

  #close(reset = false) {
    this.dialog.close();
    if (reset || this.finished) this.#reset();
    this.opener?.focus?.();
  }

  #reset() {
    this.form.reset();
    this.finished = false;
    delete this.dialog.dataset.done;
    this.selected = null;
    this.querySelector('[data-appt-date]').value = '';
    this.querySelector('[data-appt-panel="done"]').hidden = true;
    this.track.hidden = false;
    this.nextButton.hidden = false;
    this.#paintCalendar();
    for (const el of this.querySelectorAll('hs-select select')) {
      el.dispatchEvent(new CustomEvent('hs:options-changed', { bubbles: true }));
    }
    this.#go(1, false);
  }

  /* ---- Steps ---------------------------------------------------------- */

  #next() {
    const problem = this.#validate(this.step);
    if (problem) {
      this.error.textContent = problem.message;
      this.error.hidden = false;
      problem.field?.setAttribute?.('aria-invalid', 'true');
      problem.field?.focus?.();
      return;
    }
    if (this.step === LAST) this.#send();
    else this.#go(this.step + 1);
  }

  #go(step, animate = true) {
    this.step = Math.min(Math.max(step, 1), LAST);
    this.#clearError();
    if (this.step === LAST) this.#review();
    this.#place(animate);

    const current = this.panels.find((p) => p.dataset.apptPanel === String(this.step));
    /* Focus the step's heading so a screen reader announces where it is, and
     * so the next Tab lands on the step's first control. */
    const heading = current?.querySelector('.appt__step-title');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    current?.querySelector('.appt__inner')?.scrollTo?.(0, 0);
  }

  /* Where the track sits. Panels are 50% wide on a wide screen (two in
   * view: previous and current) and 100% on a narrow one. */
  #place(animate) {
    const wide = WIDE.matches;
    const index = wide ? this.step - 1 : this.step;
    const unit = wide ? 50 : 100;
    this.track.style.transition = animate && !REDUCED.matches ? '' : 'none';
    this.track.style.transform = `translateX(${-index * unit}%)`;
    if (!animate) requestAnimationFrame(() => (this.track.style.transition = ''));

    for (const panel of this.panels) {
      const n = panel.dataset.apptPanel;
      if (n === 'done') continue;
      const isCurrent = n === String(this.step);
      const isPrev = wide && n === String(this.step - 1);
      panel.inert = !isCurrent;
      panel.dataset.state = isCurrent ? 'current' : isPrev ? 'previous' : 'away';
      panel.setAttribute('aria-hidden', String(!isCurrent));
    }

    this.backButton.hidden = this.step === 1;
    this.count.textContent = this.text('step_count').replace('[step]', this.step).replace('[total]', LAST);
    this.nextButton.textContent = this.text(this.step === LAST ? 'send' : 'continue');
  }

  #validate(step) {
    const v = (name) => this.form.elements[name];
    if (step === 1 && !this.form.querySelector('[name="appt_service"]:checked')) {
      return { message: this.text('error_service') };
    }
    if (step === 3) {
      if (!v('appt_date').value) return { message: this.text('error_day') };
      if (!this.form.querySelector('[name="appt_time"]:checked')) return { message: this.text('error_time') };
      if (!this.form.querySelector('[name="appt_how"]:checked')) return { message: this.text('error_how') };
    }
    if (step === 4) {
      if (!v('appt_first').value.trim()) return { message: this.text('error_first'), field: v('appt_first') };
      const email = v('appt_email');
      if (!email.value.trim() || !email.checkValidity()) return { message: this.text('error_email'), field: email };
      const how = this.form.querySelector('[name="appt_how"]:checked')?.value;
      if (how && how === this.text('phone_way') && !v('appt_phone').value.trim()) {
        return { message: this.text('error_phone'), field: v('appt_phone') };
      }
    }
    if (step === 5 && !v('appt_consent').checked) {
      return { message: this.text('error_consent') };
    }
    return null;
  }

  #clearError() {
    this.error.hidden = true;
    for (const el of this.form.querySelectorAll('[aria-invalid]')) el.removeAttribute('aria-invalid');
  }

  /* ---- Answers -------------------------------------------------------- */

  #answers() {
    const f = this.form;
    const checked = (name) => f.querySelector(`[name="${name}"]:checked`)?.value || '';
    const all = (name) => [...f.querySelectorAll(`[name="${name}"]:checked`)].map((i) => i.value);
    const date = this.selected
      ? this.selected.toLocaleDateString(LANG, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      : '';
    return {
      service: checked('appt_service'),
      interests: all('appt_interest').join(', '),
      budget: f.elements.appt_budget.value,
      occasion: f.elements.appt_occasion.value,
      notes: f.elements.appt_notes.value.trim(),
      date,
      time: checked('appt_time'),
      how: checked('appt_how'),
      name: `${f.elements.appt_first.value.trim()} ${f.elements.appt_last.value.trim()}`.trim(),
      email: f.elements.appt_email.value.trim(),
      phone: f.elements.appt_phone.value.trim(),
      about: this.dataset.about ? `${this.dataset.about} (${this.dataset.aboutUrl})` : '',
      updates: f.elements.appt_updates.checked ? 'Yes' : 'No',
    };
  }

  #review() {
    const a = this.#answers();
    const rows = [
      [this.text('review_service'), a.service, 1],
      [this.text('review_interests'), a.interests, 2],
      [this.text('review_budget'), a.budget, 2],
      [this.text('review_occasion'), a.occasion, 2],
      [this.text('review_when'), [a.date, a.time].filter(Boolean).join(', '), 3],
      [this.text('review_how'), a.how, 3],
      [this.text('review_name'), a.name, 4],
      [this.text('review_email'), a.email, 4],
      [this.text('review_phone'), a.phone, 4],
      [this.text('review_about'), this.dataset.about || '', 0],
    ].filter(([, value]) => value);

    const list = this.querySelector('[data-appt-review]');
    list.replaceChildren(
      ...rows.flatMap(([label, value, step]) => {
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = value;
        if (step) {
          const edit = document.createElement('button');
          edit.type = 'button';
          edit.className = 'appt__edit';
          edit.textContent = this.text('change');
          edit.setAttribute('aria-label', `${this.text('change')}: ${label}`);
          edit.addEventListener('click', () => this.#go(step));
          dd.append(edit);
        }
        return [dt, dd];
      }),
    );
  }

  async #send() {
    const a = this.#answers();
    for (const out of this.querySelectorAll('[data-appt-out]')) {
      const key = out.dataset.apptOut;
      if (key === 'body') {
        out.value = [
          `Appointment request: ${a.service}`,
          `When: ${a.date}, ${a.time}. ${a.how}.`,
          a.interests && `Interested in: ${a.interests}`,
          a.budget && `Budget: ${a.budget}`,
          a.occasion && `Occasion: ${a.occasion}`,
          a.about && `Regarding: ${a.about}`,
          a.notes && `Notes: ${a.notes}`,
        ]
          .filter(Boolean)
          .join('\n');
      } else out.value = a[key] ?? '';
    }

    this.nextButton.disabled = true;
    this.nextButton.textContent = this.text('sending');

    /* The step fields are for the dialog, not for the shop's inbox: only the
     * contact[…] fields are sent, or every answer would arrive twice. */
    const body = new FormData();
    for (const [key, value] of new FormData(this.form)) {
      if (!key.startsWith('appt_')) body.append(key, value);
    }

    try {
      const res = await fetch(this.form.action, { method: 'POST', body, headers: { Accept: 'text/html' } });
      /* Shopify answers a suspicious post with its own challenge page. That
       * has to be shown to the person, so hand over to a real submit and pick
       * the flow back up when the page returns. */
      if (!res.ok || /challenge/.test(res.url)) throw new Error('challenge');
      this.#done();
    } catch {
      sessionStorage.setItem(FLAG, '1');
      for (const el of this.form.querySelectorAll('[name^="appt_"]')) el.disabled = true;
      this.form.submit();
    }
  }

  #done() {
    this.finished = true;
    this.track.hidden = false;
    for (const p of this.panels) if (p.dataset.apptPanel !== 'done') p.inert = true;
    const done = this.querySelector('[data-appt-panel="done"]');
    done.hidden = false;
    this.dialog.dataset.done = 'true';
    this.nextButton.hidden = true;
    this.nextButton.disabled = false;
    this.backButton.hidden = true;
    this.count.textContent = '';
    done.querySelector('button')?.focus();
  }

  /* ---- Calendar ------------------------------------------------------- */

  /* A preference, not a booking: tomorrow onward for the section's number of
   * days (90 by default), Sundays closed unless the section says otherwise.
   * There is no availability behind it, and the step says so. */
  #calendar() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.min = new Date(today);
    this.min.setDate(this.min.getDate() + 1);
    this.max = new Date(today);
    this.max.setDate(this.max.getDate() + (Number(this.dataset.daysAhead) || 90));
    this.view = new Date(this.min.getFullYear(), this.min.getMonth(), 1);

    this.querySelector('[data-cal-prev]').addEventListener('click', () => this.#month(-1));
    this.querySelector('[data-cal-next]').addEventListener('click', () => this.#month(1));
    this.#paintCalendar();
  }

  #month(by) {
    const next = new Date(this.view.getFullYear(), this.view.getMonth() + by, 1);
    const firstAllowed = new Date(this.min.getFullYear(), this.min.getMonth(), 1);
    const lastAllowed = new Date(this.max.getFullYear(), this.max.getMonth(), 1);
    if (next < firstAllowed || next > lastAllowed) return;
    this.view = next;
    this.#paintCalendar();
  }

  #paintCalendar() {
    const grid = this.querySelector('[data-cal-grid]');
    const year = this.view.getFullYear();
    const month = this.view.getMonth();
    this.querySelector('[data-cal-month]').textContent = this.view.toLocaleDateString(LANG, { month: 'long', year: 'numeric' });

    /* 4–10 Jan 1970 ran Sunday to Saturday: a fixed week to name the days. */
    const cells = [4, 5, 6, 7, 8, 9, 10].map((d) => {
      const el = document.createElement('span');
      el.className = 'appt__cal-dow';
      el.textContent = new Date(1970, 0, d).toLocaleDateString(LANG, { weekday: 'short' });
      el.setAttribute('aria-hidden', 'true');
      return el;
    });

    const offset = new Date(year, month, 1).getDay();
    for (let i = 0; i < offset; i++) cells.push(document.createElement('span'));

    const days = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= days; d++) {
      const date = new Date(year, month, d);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'appt__cal-day';
      button.textContent = String(d);
      button.setAttribute('aria-label', date.toLocaleDateString(LANG, { weekday: 'long', month: 'long', day: 'numeric' }));
      button.disabled = date < this.min || date > this.max || (this.dataset.closedSundays !== 'false' && date.getDay() === 0);
      if (this.selected && date.getTime() === this.selected.getTime()) button.setAttribute('aria-pressed', 'true');
      button.addEventListener('click', () => {
        this.selected = date;
        this.querySelector('[data-appt-date]').value = date.toISOString().slice(0, 10);
        this.#clearError();
        this.#paintCalendar();
        grid.querySelector('[aria-pressed="true"]')?.focus();
      });
      cells.push(button);
    }
    grid.replaceChildren(...cells);

    const firstAllowed = new Date(this.min.getFullYear(), this.min.getMonth(), 1);
    const lastAllowed = new Date(this.max.getFullYear(), this.max.getMonth(), 1);
    this.querySelector('[data-cal-prev]').disabled = this.view <= firstAllowed;
    this.querySelector('[data-cal-next]').disabled = this.view >= lastAllowed;
  }
}

if (!customElements.get('hs-appointment')) customElements.define('hs-appointment', HsAppointment);
